/**
 * Saved views: list, create, delete. Scoped to org. Payload = { version, filters, sortKey, viewType }.
 * Batched: one query per org (no N+1).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import {
  type SavedViewPayload,
  SAVED_VIEW_PAYLOAD_VERSION,
  parseSavedViewPayload,
} from '@/lib/filters';

const QUERY_KEY = 'saved-views';

interface SavedViewRow {
  id: string;
  org_id: string;
  name: string;
  payload: unknown;
  created_at: string;
}

async function fetchSavedViews(orgId: string | null): Promise<{ id: string; name: string; payload: SavedViewPayload | null; created_at: string }[]> {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from('saved_views')
    .select('id, org_id, name, payload, created_at')
    .eq('org_id', orgId)
    .order('name');
  if (error) throw error;
  return (data ?? []).map((row: SavedViewRow) => ({
    id: row.id,
    name: row.name,
    payload: parseSavedViewPayload(row.payload),
    created_at: row.created_at,
  }));
}

export function useSavedViews() {
  const { organization } = useApp();
  const orgId = organization?.id ?? null;
  const query = useQuery({
    queryKey: [QUERY_KEY, orgId],
    queryFn: () => fetchSavedViews(orgId),
    enabled: Boolean(orgId),
  });
  // Only return views with valid payload; invalid ones are ignored (fallback to default on apply).
  const views = (query.data ?? []).filter((v) => v.payload != null) as {
    id: string;
    name: string;
    payload: SavedViewPayload;
    created_at: string;
  }[];
  return {
    views,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSaveView() {
  const { organization } = useApp();
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      name,
      payload,
    }: {
      name: string;
      payload: SavedViewPayload;
    }) => {
      if (!organization?.id) throw new Error('No organization');
      const { error } = await supabase.from('saved_views').upsert(
        {
          org_id: organization.id,
          name: name.trim(),
          payload,
        },
        { onConflict: 'org_id,name' }
      );
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}

export function useDeleteSavedView() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('saved_views').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}
